import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ws } from '@/lib/ws';
import type { JobMeta } from '@shared/protocol';
import { relativeTime } from '@/lib/utils';

function statusVariant(status: JobMeta['status']) {
  switch (status) {
    case 'succeeded':
      return 'success' as const;
    case 'failed':
      return 'destructive' as const;
    case 'running':
      return 'default' as const;
    default:
      return 'secondary' as const;
  }
}

export function JobListPage() {
  const [jobs, setJobs] = useState<JobMeta[]>([]);

  useEffect(() => {
    const off = ws.on('jobs.page', (msg) => setJobs(msg.jobs));
    const offStatus = ws.on('job.status', (msg) => {
      setJobs((prev) => prev.map((j) => (j.id === msg.job_id ? { ...j, status: msg.status } : j)));
    });
    ws.send({ type: 'jobs.list' });
    return () => {
      off();
      offStatus();
    };
  }, []);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Jobs</h1>
        <Link
          to="/jobs/new"
          className="inline-flex items-center h-9 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
        >
          New job
        </Link>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Recent compile jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No jobs yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Entry</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{job.source_summary}</TableCell>
                    <TableCell className="font-mono text-xs">{job.entry}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(job.created_at)}
                    </TableCell>
                    <TableCell>
                      <Link to={`/jobs/${job.id}`} className="text-sm underline">
                        View
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
